import type { BrandId, BrandKit } from './brand.types'
import { swaashBrand } from './brands/swaash'
import { aquaVitaeBrand } from './brands/aquaVitae'
import { lowcountryMaleBrand } from './brands/lowcountryMale'
import { homelyMdBrand } from './brands/homelyMd'
import { evolveWellnessBrand } from './brands/evolveWellness'

export const BRANDS: Record<BrandId, BrandKit> = {
  swaash: swaashBrand,
  'aqua-vitae': aquaVitaeBrand,
  'lowcountry-male': lowcountryMaleBrand,
  'homely-md': homelyMdBrand,
  'evolve-wellness': evolveWellnessBrand,
}

export const DEFAULT_BRAND = swaashBrand

/**
 * Hostname → organization.
 *
 * Matched as a substring of the hostname, so every environment prefix and
 * portal suffix of a mapped host resolves to the same brand:
 *   lowcountrymale.homleyehr.com
 *   qa-lowcountrymale.homleyehr.com
 *   lowcountrymale-patient.homleyehr.com
 *
 * Anything unmapped falls back to DEFAULT_BRAND — an unmapped deployment
 * looks exactly as it did before branding was introduced.
 */
const HOST_BRANDS: ReadonlyArray<readonly [match: string, brand: BrandId]> = [
  ['lowcountrymale', 'lowcountry-male'],
  ['aquavitae', 'aqua-vitae'],
  ['evolvewellness', 'evolve-wellness'],
]

/**
 * Brand for the current URL.
 *
 * Login runs before authentication, so the organization cannot come from the
 * login response — it is read from the host. Mirrors `detectPortal()`.
 *
 * `VITE_BRAND` wins over the host when set. Dev, QA and stage each run on a
 * single host that matches no organization, so they pin the brand that way
 * (see .env.development / .env.qa / .env.stage) — an unpinned environment on
 * such a host silently renders DEFAULT_BRAND instead. Only production resolves
 * from its real per-organization hostnames.
 */
export const detectBrand = (): BrandKit => {
  const envBrand = import.meta.env.VITE_BRAND as string | undefined

  if (envBrand) {
    if (envBrand in BRANDS) return BRANDS[envBrand as BrandId]
    // Falling through to the host here would look like "branding is broken"
    // with nothing to explain why, so say so rather than fail silently.
    if (import.meta.env.DEV) {
      console.warn(
        `[branding] VITE_BRAND="${envBrand}" is not a known brand ` +
          `(${Object.keys(BRANDS).join(' | ')}). Falling back to hostname detection.`,
      )
    }
  }

  const hostname = window.location.hostname.toLowerCase()
  const match = HOST_BRANDS.find(([needle]) => hostname.includes(needle))

  return match ? BRANDS[match[1]] : DEFAULT_BRAND
}
